import { useState } from 'react';
import { DragOverEvent } from '@dnd-kit/core';

interface DraggedItemData {
  type?: string;
  sectionId?: string | number;
  sortable?: {
    items: Array<string | number>;
  };
}

/**
 * Función auxiliar para determinar el ID de la sección de destino
 * basándose en el evento de arrastre (DragOverEvent).
 */
const getTargetSectionId = (event: DragOverEvent): string | number | undefined => {
  const { over } = event;

  if (!over) {
    return undefined;
  }

  const overIdString = over.id.toString();
  const overData = over.data.current as DraggedItemData; // Aseguramos el tipo de overData

  // Caso 1: Arrastre sobre un EmptyItem
  if (overIdString.startsWith('emptyRow_')) {
    return overIdString.split('_')[1];
  }

  // Caso 2: Arrastre sobre una tarea
  if (overData?.type === 'task' && overData.sectionId !== undefined) {
    return overData.sectionId;
  }

  // Caso 3: Arrastre directamente sobre una BoardColumn (DndContext's SortableContext)
  // Busca el ID de la sección en la lista de items del droppable si está disponible
  const sortableItems = overData?.sortable?.items;
  if (sortableItems) {
    const sectionIndex = sortableItems.findIndex(
      (id: number | string) => id.toString() === overIdString
    );
    if (sectionIndex !== -1) {
      return sortableItems[sectionIndex];
    }
  }

  return undefined; // No se pudo determinar un ID de sección de destino
};


export const useDragOverStyling = () => {
  const [activeDroppable, setActiveDroppable] = useState<string | null>(null);

  const handleDragOver = (event: DragOverEvent) => {
    const { active } = event;

    const targetSectionId = getTargetSectionId(event);
    const sourceSectionId = active.data.current?.sectionId?.toString();

    if (targetSectionId !== undefined) {
      // Solo aplica el estilo si la sección de destino es diferente a la de origen
      if (targetSectionId.toString() !== sourceSectionId) {
        setActiveDroppable(targetSectionId.toString());
      } else {
        // Si es la misma sección, no hay estilo activo
        setActiveDroppable(null);
      }
    } else {
      // Si no hay 'over' o no se pudo determinar un targetSectionId válido
      setActiveDroppable(null);
    }
  };

  const resetActiveDroppable = () => {
    setActiveDroppable(null);
  };

  return {
    activeDroppable,
    handleDragOver,
    resetActiveDroppable,
  };
};